import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { COUNTRIES } from '../../data/countries';
import { getCanvasDefinition } from '../../canvases/registry';
import CanvasStage from '../../canvases/CanvasStage';
import { inclusiveDays, feeForDays, formatSgd, MAX_SERVICE_DAYS } from '../../lib/pricing';
import { TERMS_TEXT, PRIVACY_TEXT } from './legal';
import { useDemoMessages } from '../../hooks/useDemoMessages';
import './marketing.css';

const DRAFT_KEY = 'cm_order_draft';

export default function HomePage() {
  const navigate = useNavigate();
  const [canvases, setCanvases] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    canvas_library_id: '',
    customer_name: '',
    customer_organisation: '',
    customer_email: '',
    customer_country: '',
    customer_mobile: '',
    start_date: '',
    end_date: '',
    tos_accepted: false,
  });

  const demoMessages = useDemoMessages(5000, 7);

  useEffect(() => {
    fetch('/api/public-catalog')
      .then(async (res) => {
        const type = res.headers.get('content-type') || '';
        if (!res.ok || !type.includes('application/json')) {
          throw new Error('catalog unavailable');
        }
        return res.json();
      })
      .then((data) => setCanvases(data.canvases || []))
      .catch(() => setError('Unable to load canvases right now.'));
  }, []);

  const country = COUNTRIES.find((c) => c.name === form.customer_country);
  const days = inclusiveDays(form.start_date, form.end_date);
  const dateError = form.start_date && form.end_date && days > MAX_SERVICE_DAYS
    ? 'Apologies! Maximum days for our service is 50 days. Please select another date.'
    : (form.start_date && form.end_date && days < 1
      ? 'Service End Date must be on or after Service Start Date.'
      : '');
  const fee = !dateError && days ? feeForDays(days) : null;
  const datesOk = Boolean(fee);
  const selectedCanvas = canvases.find((c) => c.id === form.canvas_library_id);
  const canPay = datesOk && form.canvas_library_id && form.customer_name && form.customer_email
    && form.customer_country && form.tos_accepted;

  const setField = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (!canPay) return;
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
      ...form,
      customer_country_code: country?.dialCode || '',
      days,
      fee,
      canvas_label: selectedCanvas?.label,
      moving_object_type: selectedCanvas?.moving_object_type,
    }));
    navigate('/pay');
  };

  return (
    <div className="mkt-page">
      <header className="mkt-nav">
        <div className="mkt-logo">Canvas <span>Memories</span></div>
        <a href="#book">Book a canvas</a>
      </header>

      <section className="mkt-hero">
        <h1>A living canvas of messages, kept as a memory.</h1>
        <p>
          Guests scan the QR code and leave a note. Fireflies, koi fish, birds, and more carry their heartfelt words across a private canvas, bringing everyone's thoughts and wishes together — then we send you the finished piece to keep and treasure.
        </p>
      </section>

      <section className="mkt-section" id="book">
        <h2>Choose your canvas</h2>
        <p className="mkt-lead">Each canvas is a different way for guests to leave a message. Pick one, then tell us when your occasion runs.</p>
        {error && <div className="mkt-alert">{error}</div>}
        <div className="mkt-grid">
          {canvases.map((canvas) => {
            const def = getCanvasDefinition(canvas.moving_object_type);
            return (
              <button
                key={canvas.id}
                type="button"
                className={`mkt-canvas-card${form.canvas_library_id === canvas.id ? ' selected' : ''}`}
                onClick={() => setForm((f) => ({ ...f, canvas_library_id: canvas.id }))}
              >
                <div className="mkt-canvas-preview">
                  {def && (
                    <CanvasStage
                      createStage={def.createStage}
                      messages={demoMessages}
                      backgroundImageUrl={canvas.background_image_url}
                      interactive={true}
                    />
                  )}
                </div>
                <h3>{canvas.label}</h3>
                <p>{canvas.description}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mkt-section">
        <h2>Your details</h2>
        <p className="mkt-lead">We’ll use these to activate your canvas and email you once payment is verified.</p>
        <form className="mkt-form" onSubmit={handleNext}>
          <div className="mkt-form-grid">
            <div className="mkt-field">
              <label>Name *</label>
              <input required value={form.customer_name} onChange={setField('customer_name')} />
            </div>
            <div className="mkt-field">
              <label>Organisation (optional)</label>
              <input value={form.customer_organisation} onChange={setField('customer_organisation')} />
            </div>
            <div className="mkt-field">
              <label>Email *</label>
              <input type="email" required value={form.customer_email} onChange={setField('customer_email')} />
            </div>
            <div className="mkt-field">
              <label>Country *</label>
              <select required value={form.customer_country} onChange={setField('customer_country')}>
                <option value="">Select a country…</option>
                {COUNTRIES.map((c) => <option key={c.iso2} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div className="mkt-dates">
              <div className="mkt-field">
                <label>Service Start Date *</label>
                <input type="date" required value={form.start_date} onChange={setField('start_date')} />
              </div>
              <div className="mkt-field">
                <label>Service End Date *</label>
                <input type="date" required value={form.end_date} onChange={setField('end_date')} />
              </div>
            </div>
            <div className="mkt-field">
              <label>Mobile Number (optional)</label>
              <div className="mkt-phone">
                <span>{country?.dialCode || '—'}</span>
                <input value={form.customer_mobile} onChange={setField('customer_mobile')} placeholder="9123 4567" />
              </div>
            </div>
          </div>

          {dateError && <div className="mkt-alert">{dateError}</div>}

          {datesOk && (
            <>
              <div className="mkt-fee">
                Service fee for {days} day{days === 1 ? '' : 's'}: <strong>{formatSgd(fee)}</strong>
                <div>SGD only. Maximum booking length is 50 days.</div>
              </div>
              <div className="mkt-legal">{`${TERMS_TEXT}\n\n${PRIVACY_TEXT}`}</div>
              <label className="mkt-check">
                <input type="checkbox" checked={form.tos_accepted} onChange={setField('tos_accepted')} />
                <span>Acknowledged the Terms of Service and Privacy Policy</span>
              </label>
              {form.tos_accepted && (
                <button className="mkt-btn gold" type="submit" disabled={!canPay}>Next — Payment</button>
              )}
            </>
          )}
        </form>
      </section>

      <footer className="mkt-footer">
        <span>© {new Date().getFullYear()} The Canvas Memories Team</span>
        <Link to="/admin/login">Staff login</Link>
      </footer>
    </div>
  );
}

export { DRAFT_KEY };
